import PropTypes from 'prop-types'
import { createRef, Fragment, PureComponent } from 'react'
import { Divider, Form, Message } from 'semantic-ui-react'

import { Link, SimpleButton } from 'ui'

import ListOptionSchema from '../../../schemas/ListOptionSchema'

import styles from './index.css'

const QUESTIONS = [
  {
    key: 'player_fever',
    title: '¿Ha tenido fiebre mayor a 38°C en los últimos 3 días?',
  },
  {
    key: 'player_cough',
    title: '¿Ha tenido tos en los últimos días?',
  },
  {
    key: 'player_chest_pain',
    title: '¿Le ha dolido el pecho recientemente?',
  },
  {
    key: 'player_throat_pain',
    title: '¿Ha sentido dolor de garganta o ha tenido secreción nasal en los últimos dias?',
  },
  {
    key: 'player_trouble_breathing',
    title: '¿Le ha faltado el aire o ha tenido dificultad para respirar?',
  },
  {
    key: 'player_fatigued',
    title: '¿Se ha sentido últimamente más fatigado de los usual?',
  },
  {
    key: 'player_decrease_smell',
    title: '¿Ha notado disminución del olfato o del sabor de los alimentos?',
  },
  {
    key: 'player_abdominal_pain',
    title: '¿Ha presentado dolor abdominal, náuseas, vómito o diarrea últimamente?',
  },
  {
    key: 'player_muscle_pain',
    title: '¿Ha tenido dolores musculares o articulares recientemente?',
  },
  {
    key: 'player_covid_contact',
    title: '¿Ha estado en contacto con alguien diagnosticado o sospechoso de COVID-19?',
  },
  {
    key: 'player_other_physical_issues',
    title: '¿Tiene en este momento alguna otra razón fisica que le impida hacer ejercicio?',
  },
]

const isRequired = (val) => (`${val}`.replaceAll(/([^a-z0-9]+)/gi, '').length > 0 ? null : 'Requerido')

const isXChars = (minChars) => (val) =>
  `${val}`.replaceAll(/([^a-z0-9]+)/gi, '').length >= minChars ? null : `Incluye ${minChars} o más caracteres`

const isNIN = (val) =>
  `${val}`.replaceAll(/([^0-9]+)/gi, '').length !== `${val}`.length || `${val}`.replaceAll(/([^0-9]+)/gi, '').length < 5
    ? 'Incluye un número de indentificación sin espacios o letras'
    : null

const isPhone = (val) =>
  `${val}`.replaceAll(/([^0-9]+)/gi, '').length !== `${val}`.length ||
  `${val}`.replaceAll(/([^0-9]+)/gi, '').length !== 10
    ? 'Incluye un número celular (solo números, sin indicativo)'
    : null

const renderInfoMessage = () => (
  <Message negative>Se recomienda abstenerse de hacer deporte y consultar con su médico</Message>
)

const INMUTABLE_FIELDS = ['nin', 'first_name', 'last_name', 'phone']

class PlayerBioSecurityForm extends PureComponent {
  componentDidMount() {
    this.setState((prevState) => {
      const fields = { ...prevState.fields }
      const { values } = this.props

      Object.keys(fields).forEach((field) => {
        fields[field].value = values[field] ?? fields[field].value
        fields[field].readOnly =
          INMUTABLE_FIELDS.includes(field) &&
          values[field] !== null &&
          values[field] !== undefined &&
          values[field] !== ''
            ? !fields[field].getError(fields[field].value)
            : false

        if (fields[field].value === true || fields[field].value === false) {
          fields[field].value = +fields[field].value
        }
      })

      return { fields }
    })
  }

  el = createRef()

  state = {
    fields: {
      first_name: {
        error: null,
        getError: isXChars(3),
        value: '',
      },
      last_name: {
        error: null,
        getError: isXChars(3),
        value: '',
      },
      nin: {
        error: null,
        getError: isNIN,
        value: '',
      },
      number: {
        error: null,
        getError: Function.prototype,
        value: '',
      },
      phone: {
        error: null,
        getError: isPhone,
        value: '',
      },
      position_id: {
        error: null,
        getError: isRequired,
        value: '',
      },
      team_id: {
        error: null,
        getError: isRequired,
        value: '',
      },
      ...QUESTIONS.reduce(
        (acc, { key }) => ({
          ...acc,
          [key]: {
            error: null,
            getError: isRequired,
            value: '',
          },
        }),
        {},
      ),
    },
  }

  onChange = (_, { name, value }) =>
    this.setState(({ fields }) => ({
      fields: {
        ...fields,
        [name]: {
          ...fields[name],
          error: null,
          value,
        },
      },
    }))

  onSaveForm = () => {
    const fields = { ...this.state.fields }
    let errors = 0

    Object.keys(fields).forEach((field) => {
      fields[field].error = fields[field].getError(fields[field].value)
      errors += !!fields[field].error
    })

    if (errors) {
      this.setState({ fields }, () => this.el?.current?.querySelector('.field.error input')?.focus())
    } else {
      const data = {
        role: 'PLAYER',
      }
      Object.keys(fields).forEach((key) => {
        data[key] = fields[key].value
      })
      this.props.onSave(data, 'Has enviado el formulario')
    }
  }

  shouldRenderInfoMessage = () => QUESTIONS.some(({ key }) => this.state.fields[key].value === 1)

  render() {
    const { asAgent, className, positions, teams } = this.props
    const { fields } = this.state

    return (
      <div className={className} ref={this.el}>
        <h3>
          Cuestionario de Riesgo COVID
          <Divider />
          {asAgent ? 'Información del jugador representado' : 'Tu información como participante'}
        </h3>
        <p>
          Ten cuidado al llenar la información pues
          <b className={styles.redText}> no podrá ser cambiada</b>
        </p>
        <Form as="form">
          <Form.Group>
            <Form.Input
              error={fields.first_name.error}
              label={asAgent ? 'Nombre(s) del jugador' : 'Tu nombre'}
              name="first_name"
              onChange={this.onChange}
              readOnly={fields.first_name.readOnly}
              required
              transparent={fields.first_name.readOnly}
              type="text"
              value={fields.first_name.value}
              width={4}
            />
            <Form.Input
              error={fields.last_name.error}
              label={asAgent ? 'Apellido(s) del jugador' : 'Tu apellido'}
              name="last_name"
              onChange={this.onChange}
              readOnly={fields.last_name.readOnly}
              required
              transparent={fields.last_name.readOnly}
              type="text"
              value={fields.last_name.value}
              width={4}
            />
          </Form.Group>
          <Form.Group>
            <Form.Input
              error={fields.nin.error}
              label={asAgent ? 'Cédula NUIP del jugador' : 'Tu Cédula NUIP'}
              name="nin"
              onChange={this.onChange}
              readOnly={fields.nin.readOnly}
              required
              transparent={fields.nin.readOnly}
              type="text"
              value={fields.nin.value}
              width={4}
            />
            <Form.Input
              error={fields.phone.error}
              label={asAgent ? 'Teléfono del jugador' : 'Tu teléfono'}
              name="phone"
              onChange={this.onChange}
              readOnly={fields.phone.readOnly}
              required
              transparent={fields.phone.readOnly}
              type="tel"
              value={fields.phone.value}
              width={4}
            />
          </Form.Group>
          <Form.Group>
            <Form.Select
              error={fields.team_id.error}
              label={asAgent ? 'Equipo del jugador' : 'Tu equipo'}
              name="team_id"
              onChange={this.onChange}
              options={teams.map((p) => ({ key: p.id, text: p.name, value: p.id }))}
              placeholder="Equipo"
              readOnly={fields.team_id.readOnly}
              required
              value={fields.team_id.value}
              width={4}
            />
            <Form.Select
              error={fields.position_id.error}
              label={asAgent ? 'Posición del jugador' : 'Tu posición'}
              name="position_id"
              onChange={this.onChange}
              options={positions.map((p) => ({ key: p.id, text: p.name, value: p.id }))}
              placeholder="Posición"
              readOnly={fields.position_id.readOnly}
              required
              value={fields.position_id.value}
              width={4}
            />
            <Form.Input
              error={fields.number.error}
              label={asAgent ? 'Número de camiseta del jugador' : 'Tu número de camiseta'}
              max={100}
              min={0}
              name="number"
              onChange={this.onChange}
              readOnly={fields.number.readOnly}
              transparent={fields.number.readOnly}
              type="number"
              value={fields.number.value || ''}
              width={4}
            />
          </Form.Group>
          <h3>
            Debes responder todo el cuestionario de riesgo para COVID-19
            <span className={styles.titleComment}>
              <Link
                as="a"
                href="https://www.minsalud.gov.co/Normatividad_Nuevo/Resoluci%C3%B3n%201840%20de%202020.pdf"
                target="_blank"
              >
                * resolución 1840 de 2020 de minsalud.gov.co
              </Link>
            </span>
          </h3>
          {QUESTIONS.map(({ key, title }) => (
            <Fragment key={key}>
              <h4>{title}</h4>
              <Form.Group inline>
                <Form.Radio
                  checked={fields[key].value === 1}
                  error={!!fields[key].error}
                  label="Sí"
                  name={key}
                  onChange={this.onChange}
                  readOnly={fields[key].readOnly}
                  value={1}
                />
                <Form.Radio
                  checked={fields[key].value === 0}
                  error={fields[key].error}
                  label="No"
                  name={key}
                  onChange={this.onChange}
                  readOnly={fields[key].readOnly}
                  value={0}
                />
              </Form.Group>
            </Fragment>
          ))}
          {this.shouldRenderInfoMessage() && renderInfoMessage()}
          <small>
            Enviando este formulario aceptas los términos y condiciones de tratamiento de datos de SweatBits
            <br />
          </small>
          <SimpleButton onClick={this.onSaveForm} primary value="Guardar cuestionario" />
        </Form>
      </div>
    )
  }
}

PlayerBioSecurityForm.propTypes = {
  onSave: PropTypes.func.isRequired,
  positions: PropTypes.arrayOf(ListOptionSchema).isRequired,
  teams: PropTypes.arrayOf(ListOptionSchema).isRequired,

  asAgent: PropTypes.bool,
  className: PropTypes.string,
  values: PropTypes.shape({}),
}

PlayerBioSecurityForm.defaultProps = {
  asAgent: false,
  className: '',
  values: {},
}

export default PlayerBioSecurityForm
